import React, { useState, useEffect, useCallback } from "react";
import {
  FileText,
  ArrowLeft,
  Users,
  Building2,
  Upload,
  Search,
  Loader2,
  RefreshCw,
  Trash2,
  CheckCircle,
  ChevronDown,
  Filter,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { Label } from "../../../foundation/altzero_base/base/components/ui/label";
import { Textarea } from "../../../foundation/altzero_base/base/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../foundation/altzero_base/base/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../foundation/altzero_base/base/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../foundation/altzero_base/base/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../../../foundation/altzero_base/base/components/ui/command";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { knowledgeService } from "../../knowledge/knowledgebase/features/knowledge/services/knowledgeService";
import { enhancedClientService } from "../services/enhancedClientService";
import ScopingAILayout from "../components/ScopingAILayout";
import { Link } from "react-router-dom";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

// Category and subcategory definitions
const ORGANISATION_CATEGORIES = {
  "Organizational Knowledge & Historical Data": [
    "Previous Proposals",
    "Company Policies",
    "Internal Templates",
    "Lessons Learned & Best Practices"
  ],
  "Internal & General Resources": [
    "Market Research",
    "Competitive Analysis",
    "Internal Communications & Memos",
    "Vendor & Partner Information",
    "Training Materials"
  ]
};

const CLIENT_CATEGORIES = {
  "Requirements & Specifications": [
    "RFP (Request for Proposal)",
    "RFQ (Request for Quote)",
    "RFI (Request for Information)",
    "BRD (Business Requirements Document)",
    "SRS (Software Requirements Specification)",
    "FRS (Functional Requirements Specification)",
    "Use Cases",
    "User Stories"
  ],
  "Project Management": [
    "Project Charter",
    "SOW (Statement of Work)",
    "Project Plans",
    "Timelines",
    "Meeting Minutes",
    "Change Orders",
    "Change Requests",
    "Risk Logs",
    "Stakeholder Analysis"
  ],
  "Communications & Legal": [
    "Client Communications",
    "Emails",
    "MSA (Master Service Agreement)",
    "Legal Contracts",
    "NDA (Non-Disclosure Agreement)",
    "Interview Notes"
  ],
  "Financial": [
    "Pricing Proposals",
    "Quotes",
    "Invoices"
  ],
  "Technical & Design": [
    "Technical Specifications",
    "Architecture Diagrams",
    "Design Documents",
    "Test Plans",
    "Test Cases",
    "User Manuals"
  ],
  "General & Supporting Documents": [
    "Market Research",
    "Competitive Analysis",
    "Internal Memos",
    "User Feedback",
    "Post-Mortem Reports"
  ]
};

interface Document {
  id: string;
  name: string;
  type: string;
  size: number;
  status: string;
  uploadedAt: string;
  description?: string;
  category?: string;
  subCategory?: string;
  clientName?: string;
  metadata?: any;
}

interface Client {
  id: string;
  name: string;
  email?: string;
  company?: string;
}

export default function ScopingKnowledgeBase() {
  const [activeTab, setActiveTab] = useState("users");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: any}>({});
  const { toast } = useToast();

  // Upload form state
  const [uploadDescription, setUploadDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [isClientOpen, setIsClientOpen] = useState(false);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");



  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    loadDocuments();
    loadClients();
  }, []);

  // Get organisation_id from current user
  const getCurrentUserOrganisationId = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return null;

      const { data: orgMember } = await supabase
        .from('organisation_members')
        .select('organisation_id')
        .eq('user_id', session.user.id)
        .single();

      return orgMember?.organisation_id || null;
    } catch (error) {
      console.error('Error getting organisation ID:', error);
      return null;
    }
  };

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      console.log("🔄 Loading documents...");
      const docs = await knowledgeService.getAllDocuments();
      console.log("📋 Raw documents from API:", docs);
      console.log("📋 Number of documents received:", docs.length);

      const transformedDocs = docs.map((doc: any) => {
        // Ensure metadata exists and has proper structure
        const metadata = doc.metadata || {};

        return {
          id: doc.id,
          name: doc.name || "Untitled Document",
          type: doc.type || "Unknown",
          size: doc.size || 0,
          status: doc.status || "unknown",
          uploadedAt: doc.uploadedAt || new Date().toISOString(),
          description: doc.description,
          category: metadata.category || "",
          subCategory: metadata.subCategory || "",
          clientName: metadata.clientName || "",
          metadata: {
            ...metadata,
            customTags: metadata.customTags || []
          },
        };
      });

      console.log("📋 Transformed documents:", transformedDocs);
      console.log("📋 Documents with companyId:", transformedDocs.filter(d => d.metadata?.companyId));
      console.log("📋 Documents without companyId:", transformedDocs.filter(d => !d.metadata?.companyId));
      console.log("📋 Documents with customTags:", transformedDocs.filter(d => d.metadata?.customTags?.length > 0));

      setDocuments(transformedDocs);
      console.log(`✅ Loaded ${transformedDocs.length} documents successfully`);
    } catch (error) {
      console.error("❌ Failed to load documents:", error);
      toast({
        title: "Error",
        description: "Failed to load documents",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const clientsData = await enhancedClientService.getOrganizationContacts();
      setClients(clientsData);
      console.log(`✅ Loaded ${clientsData.length} clients`);
    } catch (error) {
      console.error("Error loading clients:", error);
      toast({
        title: "Error",
        description: "Failed to load clients",
        variant: "destructive",
      });
    }
  };



  // Helper functions
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get current categories based on active tab
  const getCurrentCategories = () => {
    return activeTab === "users" ? ORGANISATION_CATEGORIES : CLIENT_CATEGORIES;
  };

  // Get available subcategories based on selected category
  const getSubCategories = (category: string): string[] => {
    const categories = getCurrentCategories();
    return categories[category as keyof typeof categories] || [];
  };

  // Get available tags from loaded documents
  const getAvailableTags = (): string[] => {
    const allTags = new Set<string>();
    documents.forEach((doc) => {
      if (doc.metadata?.customTags && Array.isArray(doc.metadata.customTags)) {
        doc.metadata.customTags.forEach((tag: string) => {
          if (tag && tag.trim()) {
            allTags.add(tag.trim());
          }
        });
      }
    });
    return Array.from(allTags).sort();
  };

  // Tag management functions
  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !customTags.includes(trimmedTag)) {
      const newTags = [...customTags, trimmedTag];
      setCustomTags(newTags);
      console.log("Added new tag:", trimmedTag);
      console.log("Updated customTags array:", newTags);
    } else {
      console.log("Tag not added:", { 
        trimmedTag, 
        isEmpty: !trimmedTag, 
        alreadyExists: customTags.includes(trimmedTag) 
      });
    }
    setTagInput("");
  };

  const removeTag = (tagToRemove: string) => {
    setCustomTags(customTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      console.log("Adding tag from keyboard event:", tagInput);
      addTag(tagInput);
    } else if (e.key === 'Backspace' && tagInput === '' && customTags.length > 0) {
      const tagToRemove = customTags[customTags.length - 1];
      console.log("Removing last tag:", tagToRemove);
      removeTag(tagToRemove);
    }
  };

  // Pagination helper functions
  const getPaginatedDocuments = (docs: Document[]) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return docs.slice(startIndex, endIndex);
  };

  const getTotalPages = (totalItems: number) => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset subcategory when category changes
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setSelectedSubCategory("");
    setCurrentPage(1); // Reset pagination
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, subCategoryFilter, clientFilter]);

  // Reset filters and form when switching tabs
  useEffect(() => {
    console.log(`🔄 Tab switched to: ${activeTab}`);

    // Reset all filters when switching tabs
    setSearchQuery("");
    setCategoryFilter("");
    setSubCategoryFilter("");
    setClientFilter("");

    setCurrentPage(1);

    // Reset upload form
    setSelectedFiles([]);
    setUploadDescription("");
    setSelectedCategory("");
    setSelectedSubCategory("");
    setSelectedClient("");
    setCustomTags([]);
    setTagInput("");
  }, [activeTab]);

  // File selection handler (just stores files, doesn't upload)
  const handleFileSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    console.log("📁 Files selected:", Array.from(files).map(f => f.name));
    setSelectedFiles(Array.from(files));

    // Clear the input so same files can be selected again if needed
    event.target.value = '';
  }, []);

  // Actual upload handler (triggered by upload button)
  const handleActualUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    if (!selectedCategory) {
      toast({
        title: "Category Required",
        description: "Please select a category before uploading",
      });
      return;
    }

    // For clients tab, ensure client is selected
    if (activeTab === "clients" && !selectedClient) {
      toast({
        title: "Error",
        description: "Please select a client before uploading",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress({});

    try {
      const organisationId = await getCurrentUserOrganisationId();
      if (!organisationId) {
        throw new Error("Organisation ID not found");
      }

      // Prepare metadata based on tab
      // Log the current state of customTags
      console.log("Current customTags:", customTags);

      let baseMetadata: any;

      // Log the current state of customTags before creating metadata
      console.log("Current customTags state:", customTags);

      if (activeTab === "clients" && selectedClient) {
        // Clients tab: use selected client's ID as companyId
        baseMetadata = {
          companyId: selectedClient,
          category: selectedCategory,
          subCategory: selectedSubCategory,
          aiExtractionInstructions: uploadDescription || "Uploaded from Scoping Knowledge Base",
          organisationId: organisationId,
          customTags: [...customTags] // Create a new array from customTags
        };
      } else {
        // Users tab: use default companyId
        baseMetadata = {
          category: selectedCategory,
          subCategory: selectedSubCategory,
          aiExtractionInstructions: uploadDescription || "Uploaded from Scoping Knowledge Base",
          organisationId: organisationId,
          customTags: [...customTags] // Create a new array from customTags
        };
      }

      // Log the metadata being sent
      console.log("Metadata being sent:", baseMetadata);

      const metadata = baseMetadata;

      console.log("🔍 DEBUG: customTags array:", customTags);
      console.log("🔍 DEBUG: customTags length:", customTags.length);
      console.log("🔍 DEBUG: tagInput value:", tagInput);
      console.log("🔍 DEBUG: metadata.customTags:", metadata.customTags);
      console.log("🔍 DEBUG: metadata.customTags length:", metadata.customTags?.length);

      // CRITICAL: Ensure customTags are not empty
      if (!metadata.customTags || metadata.customTags.length === 0) {
        console.warn("⚠️ WARNING: customTags is empty in metadata!");
        console.warn("⚠️ customTags state:", customTags);
        console.warn("⚠️ Will use customTags from state directly");
        metadata.customTags = [...customTags];
      }

      console.log("Upload payload:", {
        files: selectedFiles.map(f => f.name),
        metadata: {
          companyId: metadata.companyId,
          category: metadata.category,
          subCategory: metadata.subCategory,
          aiExtractionInstructions: metadata.aiExtractionInstructions,
          organisationId: metadata.organisationId,
          customTags: metadata.customTags
        },
        tab: activeTab
      });

      console.log("Metadata being sent:", metadata);

      const response = await knowledgeService.uploadDocuments(
        selectedFiles,
        (progressData) => {
          // Update progress for each file
          setUploadProgress(prev => ({
            ...prev,
            ...progressData
          }));
        },
        metadata
      );

      if (response.documents && response.documents.length > 0) {
        toast({
          title: "Success",
          description: `${response.documents.length} document(s) uploaded successfully`,
        });

        // Multiple reloads to ensure documents appear
        const reloadDocuments = async () => {
          await loadDocuments();
          setTimeout(async () => {
            await loadDocuments();
            setTimeout(async () => {
              await loadDocuments();
            }, 2000);
          }, 1000);
        };

        await reloadDocuments();

        // Reset form after successful upload
        setTimeout(() => {
          setSelectedFiles([]);
          setUploadDescription("");
          setSelectedCategory("");
          setSelectedSubCategory("");
          setCustomTags([]);
          setTagInput("");
          if (activeTab === "clients") {
            setSelectedClient("");
          }
        }, 500);
      }

      if (response.errors && response.errors.length > 0) {
        toast({
          title: "Warning",
          description: `Some uploads failed: ${response.errors.map((e: any) => e.error).join(", ")}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Upload failed",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  }, [selectedFiles, selectedCategory, selectedSubCategory, uploadDescription, activeTab, selectedClient, clients, toast, customTags]);

  // Delete document handler
  const handleDeleteDocument = async (documentId: string) => {
    try {
      await knowledgeService.deleteDocument(documentId);
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
    } catch (error) {
      console.error("Failed to delete document:", error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  return (
    <ScopingAILayout>
      <div className="w-full px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/tender/resource-library"
            className="flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Resource Library
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center">
                Scoping Knowledge Base
              </h1>
              <p className="text-muted-foreground mt-1">
                Upload and manage documents for your scoping projects with AI-powered analysis.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={loadDocuments}
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {isLoading ? "Loading..." : "Refresh"}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Organisation Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Client Knowledge Base
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Upload Documents</CardTitle>
                <CardDescription>
                  Upload documents to your knowledge base for AI analysis and scoping assistance.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Enter document description or AI extraction instructions..."
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom-tags">Custom Tags</Label>
                      <div className="space-y-2">
                        {/* Display existing tags */}
                        {customTags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {customTags.map((tag, index) => (
                              <div
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 text-violet-800 text-sm rounded-md"
                              >
                                <span>{tag}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    removeTag(tag);
                                  }}
                                  className="text-violet-600 hover:text-violet-800"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Input for new tags */}
                        <Input
                          id="custom-tags"
                          placeholder="Type a tag and press Enter or comma to add"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagInputKeyDown}
                          onBlur={() => {
                            if (tagInput.trim()) {
                              addTag(tagInput);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(getCurrentCategories()).map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedCategory && (
                      <div className="space-y-2">
                        <Label htmlFor="subcategory">Sub Category</Label>
                        <Select value={selectedSubCategory} onValueChange={setSelectedSubCategory}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select sub category" />
                          </SelectTrigger>
                          <SelectContent>
                            {getSubCategories(selectedCategory).map((subCategory) => (
                              <SelectItem key={subCategory} value={subCategory}>
                                {subCategory}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                {/* File Upload */}
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Upload Documents</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select files to upload to your knowledge base
                  </p>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.md,.rtf,.odt"
                    onChange={handleFileSelection}
                    disabled={isUploading}
                    className="hidden"
                    id="file-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploading}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('file-upload')?.click();
                    }}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Choose Files
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    Supports: PDF, DOC, DOCX, TXT, MD, RTF, ODT (Max 50MB)
                  </p>
                </div>

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-100 p-1.5 rounded-lg">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <h4 className="text-sm font-medium">
                          Selected Files ({selectedFiles.length})
                        </h4>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedFiles([])}
                        className="text-red-600 hover:text-red-700"
                      >
                        Clear All
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <div>
                              <p className="text-sm font-medium">{file.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedFiles(files => files.filter((_, i) => i !== index));
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Upload Button */}
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        onClick={handleActualUpload}
                        disabled={isUploading || !selectedCategory || (activeTab === "clients" && !selectedClient)}
                        className="flex-1"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Show current tags */}
                    {customTags.length > 0 && (
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-sm font-medium text-purple-800 mb-2">Custom Tags:</p>
                        <div className="flex flex-wrap gap-2">
                          {customTags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Progress */}
                {(isUploading || Object.keys(uploadProgress).length > 0) && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 p-1.5 rounded-lg">
                          <Upload className="w-4 h-4 text-primary" />
                        </div>
                        <h4 className="text-sm font-medium">
                          Upload Progress
                        </h4>
                      </div>
                      <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                        {Object.keys(uploadProgress).length} file(s)
                      </div>
                    </div>

                    {Object.entries(uploadProgress).map(([documentId, progress]: [string, any]) => (
                      <div
                        key={documentId}
                        className="space-y-2 p-4 bg-muted/50 rounded-lg border"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                progress.status === "uploading"
                                  ? "bg-primary animate-pulse"
                                  : progress.status === "processing"
                                  ? "bg-yellow-500 animate-pulse"
                                  : progress.status === "success"
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {progress.message || "Processing..."}
                              </p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {progress.status}
                              </p>
                            </div>
                          </div>
                          {progress.progress && (
                            <div className="text-xs text-muted-foreground">
                              {Math.round(progress.progress)}%
                            </div>
                          )}
                        </div>
                        {progress.progress && (
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div
                              className="bg-primary h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${progress.progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents Display */}
            <Card>
              <CardHeader>
                <CardTitle>Documents</CardTitle>
                <CardDescription>
                  Manage and search through your uploaded documents.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-48">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {Object.keys(getCurrentCategories()).map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={subCategoryFilter} onValueChange={setSubCategoryFilter}>
                      <SelectTrigger className="w-48">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Filter by sub category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sub Categories</SelectItem>
                        {categoryFilter && getSubCategories(categoryFilter).map((subCategory) => (
                          <SelectItem key={subCategory} value={subCategory}>
                            {subCategory}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Documents Table */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="ml-2">Loading documents...</span>
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No documents found</h3>
                    <p className="text-muted-foreground">
                      Upload your first document to get started
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-md border">
                    <table className="w-full border-collapse bg-white text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-3 text-left font-medium">Document</th>
                          <th className="px-4 py-3 text-left font-medium">Category</th>
                          <th className="px-4 py-3 text-left font-medium">Sub Category</th>
                          <th className="px-4 py-3 text-left font-medium">Tags</th>
                          <th className="px-4 py-3 text-left font-medium">Date</th>
                          <th className="px-4 py-3 text-right font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filteredDocs = documents.filter(doc => {
                            const hasNoCompanyId = !doc.metadata?.companyId || doc.metadata?.companyId === "default";
                            const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
                            const matchesCategory = !categoryFilter || categoryFilter === "all" || doc.metadata?.category === categoryFilter;
                            const matchesSubCategory = !subCategoryFilter || subCategoryFilter === "all" || doc.metadata?.subCategory === subCategoryFilter;
                            return hasNoCompanyId && matchesSearch && matchesCategory && matchesSubCategory;
                          });
                          return getPaginatedDocuments(filteredDocs);
                        })().map((doc) => (
                          <tr key={doc.id} className="border-b hover:bg-muted/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{doc.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {formatFileSize(doc.size)}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="secondary">
                                {doc.metadata?.category || "Uncategorized"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {doc.metadata?.subCategory ? (
                                <Badge variant="outline">
                                  {doc.metadata?.subCategory}
                                </Badge>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {doc.metadata?.customTags && doc.metadata.customTags.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {doc.metadata.customTags.slice(0, 3).map((tag: string, index: number) => (
                                    <Badge key={index} variant="secondary" className="text-xs bg-violet-100 text-violet-800">
                                      {tag}
                                    </Badge>
                                  ))}
                                  {doc.metadata.customTags.length > 3 && (
                                    <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                                      +{doc.metadata.customTags.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {formatDate(doc.uploadedAt)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDeleteDocument(doc.id);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {(() => {
                      const filteredDocs = documents.filter(doc => {
                        const hasNoCompanyId = !doc.metadata?.companyId || doc.metadata?.companyId === "default";
                        const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
                        const matchesCategory = !categoryFilter || categoryFilter === "all" || doc.metadata?.category === categoryFilter;
                        const matchesSubCategory = !subCategoryFilter || subCategoryFilter === "all" || doc.metadata?.subCategory === subCategoryFilter;
                        return hasNoCompanyId && matchesSearch && matchesCategory && matchesSubCategory;
                      });
                      const totalPages = getTotalPages(filteredDocs.length);
                      console.log("🔍 DEBUG Users Pagination:", {
                        filteredDocsLength: filteredDocs.length,
                        totalPages,
                        currentPage,
                        itemsPerPage
                      });

                      if (filteredDocs.length > 0) {
                        return (
                          <div className="flex items-center justify-between mt-4">
                            <div className="text-sm text-muted-foreground">
                              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredDocs.length)} of {filteredDocs.length} documents
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage - 1);
                                }}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </Button>
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <Button
                                  key={page}
                                  type="button"
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(page);
                                  }}
                                  className="w-8 h-8 p-0"
                                >
                                  {page}
                                </Button>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage + 1);
                                }}
                                disabled={currentPage === totalPages}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clients" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Upload Client Documents</CardTitle>
                <CardDescription>
                  Upload documents specific to your clients for organized project management.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="client-description">Description</Label>
                      <Textarea
                        id="client-description"
                        placeholder="Enter document description or AI extraction instructions..."
                        value={uploadDescription}
                        onChange={(e) => setUploadDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="client-custom-tags">Custom Tags</Label>
                      <div className="space-y-2">
                        {/* Display existing tags */}
                        {customTags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {customTags.map((tag, index) => (
                              <div
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 text-violet-800 text-sm rounded-md"
                              >
                                <span>{tag}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    removeTag(tag);
                                  }}
                                  className="text-violet-600 hover:text-violet-800"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Input for new tags */}
                        <Input
                          id="client-custom-tags"
                          placeholder="Type a tag and press Enter or comma to add"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagInputKeyDown}
                          onBlur={() => {
                            if (tagInput.trim()) {
                              addTag(tagInput);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="client-select">Select Client *</Label>
                      <Popover open={isClientOpen} onOpenChange={setIsClientOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={isClientOpen}
                            className="w-full justify-between"
                          >
                            {selectedClient
                              ? clients.find((client) => client.id === selectedClient)?.name
                              : "Select client..."}
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Search clients..." />
                            <CommandEmpty>No client found.</CommandEmpty>
                            <CommandGroup>
                              {clients.map((client) => (
                                <CommandItem
                                  key={client.id}
                                  value={client.id}
                                  onSelect={(currentValue) => {
                                    setSelectedClient(currentValue === selectedClient ? "" : currentValue);
                                    setIsClientOpen(false);
                                  }}
                                >
                                  <CheckCircle
                                    className={`mr-2 h-4 w-4 ${
                                      selectedClient === client.id ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                  <div>
                                    <p className="font-medium">{client.name}</p>
                                    {client.email && (
                                      <p className="text-sm text-muted-foreground">{client.email}</p>
                                    )}
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="client-category">Category *</Label>
                      <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(getCurrentCategories()).map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedCategory && (
                    <div className="space-y-2">
                      <Label htmlFor="client-subcategory">Sub Category</Label>
                        <Select value={selectedSubCategory} onValueChange={setSelectedSubCategory}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select sub category" />
                        </SelectTrigger>
                        <SelectContent>
                            {getSubCategories(selectedCategory).map((subCategory) => (
                            <SelectItem key={subCategory} value={subCategory}>
                              {subCategory}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}
                  </div>
                </div>

                {/* File Upload for Clients */}
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Upload Client Documents</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select files to upload for the selected client
                  </p>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.md,.rtf,.odt"
                    onChange={handleFileSelection}
                    disabled={isUploading}
                    className="hidden"
                    id="client-file-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploading}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('client-file-upload')?.click();
                    }}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Choose Files
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    Supports: PDF, DOC, DOCX, TXT, MD, RTF, ODT (Max 50MB)
                  </p>
                </div>

                {/* Selected Files Preview for Clients */}
                {selectedFiles.length > 0 && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-100 p-1.5 rounded-lg">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <h4 className="text-sm font-medium">
                          Selected Files ({selectedFiles.length})
                        </h4>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedFiles([])}
                        className="text-red-600 hover:text-red-700"
                      >
                        Clear All
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <div>
                              <p className="text-sm font-medium">{file.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedFiles(files => files.filter((_, i) => i !== index));
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Upload Button for Clients */}
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        onClick={handleActualUpload}
                        disabled={isUploading || !selectedCategory || !selectedClient}
                        className="flex-1"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''} to {clients.find(c => c.id === selectedClient)?.name || 'Client'}
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Show current tags for clients */}
                    {customTags.length > 0 && (
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-sm font-medium text-purple-800 mb-2">Custom Tags:</p>
                        <div className="flex flex-wrap gap-2">
                          {customTags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Progress for Clients */}
                {(isUploading || Object.keys(uploadProgress).length > 0) && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 p-1.5 rounded-lg">
                          <Upload className="w-4 h-4 text-primary" />
                        </div>
                        <h4 className="text-sm font-medium">
                          Upload Progress
                        </h4>
                      </div>
                      <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                        {Object.keys(uploadProgress).length} file(s)
                      </div>
                    </div>

                    {Object.entries(uploadProgress).map(([documentId, progress]: [string, any]) => (
                      <div
                        key={documentId}
                        className="space-y-2 p-4 bg-muted/50 rounded-lg border"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                progress.status === "uploading"
                                  ? "bg-primary animate-pulse"
                                  : progress.status === "processing"
                                  ? "bg-yellow-500 animate-pulse"
                                  : progress.status === "success"
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">
                                {progress.message || "Processing..."}
                              </p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {progress.status}
                              </p>
                            </div>
                          </div>
                          {progress.progress && (
                            <div className="text-xs text-muted-foreground">
                              {Math.round(progress.progress)}%
                            </div>
                          )}
                        </div>
                        {progress.progress && (
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div
                              className="bg-primary h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${progress.progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Documents Display */}
            <Card>
              <CardHeader>
                <CardTitle>Client Documents</CardTitle>
                <CardDescription>
                  Manage and search through client-specific documents.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search for Clients */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Select value={clientFilter} onValueChange={setClientFilter}>
                      <SelectTrigger className="w-48">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Filter by client" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.name}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-48">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {Object.keys(getCurrentCategories()).map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={subCategoryFilter} onValueChange={setSubCategoryFilter}>
                      <SelectTrigger className="w-48">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Filter by sub category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sub Categories</SelectItem>
                        {categoryFilter && getSubCategories(categoryFilter).map((subCategory) => (
                          <SelectItem key={subCategory} value={subCategory}>
                            {subCategory}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Client Documents Table */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="ml-2">Loading documents...</span>
                  </div>
                ) : documents.filter(doc => {
                  const hasCompanyId = doc.metadata?.companyId && doc.metadata?.companyId !== "default";
                  const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
                  const clientName = clients.find(client => client.id === doc.metadata?.companyId)?.name;
                  const matchesClient = !clientFilter || clientFilter === "all" || clientName === clientFilter;
                  const matchesCategory = !categoryFilter || categoryFilter === "all" || doc.metadata?.category === categoryFilter;
                  const matchesSubCategory = !subCategoryFilter || subCategoryFilter === "all" || doc.metadata?.subCategory === subCategoryFilter;
                  return hasCompanyId && matchesSearch && matchesClient && matchesCategory && matchesSubCategory;
                }).length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No client documents found</h3>
                    <p className="text-muted-foreground">
                      Upload your first client document to get started
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-md border">
                    <table className="w-full border-collapse bg-white text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-3 text-left font-medium">Client</th>
                          <th className="px-4 py-3 text-left font-medium">Document</th>
                          <th className="px-4 py-3 text-left font-medium">Category</th>
                          <th className="px-4 py-3 text-left font-medium">Sub Category</th>
                          <th className="px-4 py-3 text-left font-medium">Tags</th>
                          <th className="px-4 py-3 text-left font-medium">Date</th>
                          <th className="px-4 py-3 text-right font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filteredDocs = documents.filter(doc => {
                            const hasCompanyId = doc.metadata?.companyId && doc.metadata?.companyId !== "default";
                            const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
                            const clientName = clients.find(client => client.id === doc.metadata?.companyId)?.name;
                            const matchesClient = !clientFilter || clientFilter === "all" || clientName === clientFilter;
                            const matchesCategory = !categoryFilter || categoryFilter === "all" || doc.metadata?.category === categoryFilter;
                            const matchesSubCategory = !subCategoryFilter || subCategoryFilter === "all" || doc.metadata?.subCategory === subCategoryFilter;
                            return hasCompanyId && matchesSearch && matchesClient && matchesCategory && matchesSubCategory;
                          });
                          return getPaginatedDocuments(filteredDocs);
                        })().map((doc) => (
                          <tr key={doc.id} className="border-b hover:bg-muted/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">
                                  {clients.find(client => client.id === doc.metadata?.companyId)?.name || "No Client"}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{doc.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {formatFileSize(doc.size)}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="secondary">
                                {doc.metadata?.category || "Uncategorized"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              {doc.metadata?.subCategory ? (
                                <Badge variant="outline">
                                  {doc.metadata?.subCategory}
                                </Badge>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {doc.metadata?.customTags && doc.metadata.customTags.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {doc.metadata.customTags.slice(0, 3).map((tag: string, index: number) => (
                                    <Badge key={index} variant="secondary" className="text-xs bg-violet-100 text-violet-800">
                                      {tag}
                                    </Badge>
                                  ))}
                                  {doc.metadata.customTags.length > 3 && (
                                    <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600">
                                      +{doc.metadata.customTags.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {formatDate(doc.uploadedAt)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDeleteDocument(doc.id);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {(() => {
                      const filteredDocs = documents.filter(doc => {
                        const hasCompanyId = doc.metadata?.companyId && doc.metadata?.companyId !== "default";
                        const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
                        const clientName = clients.find(client => client.id === doc.metadata?.companyId)?.name;
                        const matchesClient = !clientFilter || clientFilter === "all" || clientName === clientFilter;
                        const matchesCategory = !categoryFilter || categoryFilter === "all" || doc.metadata?.category === categoryFilter;
                        const matchesSubCategory = !subCategoryFilter || subCategoryFilter === "all" || doc.metadata?.subCategory === subCategoryFilter;
                        return hasCompanyId && matchesSearch && matchesClient && matchesCategory && matchesSubCategory;
                      });
                      const totalPages = getTotalPages(filteredDocs.length);
                      console.log("🔍 DEBUG Clients Pagination:", {
                        filteredDocsLength: filteredDocs.length,
                        totalPages,
                        currentPage,
                        itemsPerPage
                      });

                      if (filteredDocs.length > 0) {
                        return (
                          <div className="flex items-center justify-between mt-4">
                            <div className="text-sm text-muted-foreground">
                              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredDocs.length)} of {filteredDocs.length} documents
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage - 1);
                                }}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </Button>
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <Button
                                  key={page}
                                  type="button"
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(page);
                                  }}
                                  className="w-8 h-8 p-0"
                                >
                                  {page}
                                </Button>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage + 1);
                                }}
                                disabled={currentPage === totalPages}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </ScopingAILayout>
  );
}
